import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiInstance } from 'src/api/apiInstance';

export const updateAdminConfig = createAsyncThunk('admin/updateConfig', async (config: any, { rejectWithValue }) => {
  try {
    const response = await apiInstance.patch('/admin/config', config);

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const inviteNewUser = createAsyncThunk('admin/inviteNewUser', async (email: string, { rejectWithValue }) => {
  try {
    const response = await apiInstance.post('/admin/members/invite', { email });

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const getAdminMembers = createAsyncThunk('admin/getMembers', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.get('/admin/members');

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const getCustomers = createAsyncThunk(
  'admin/getCustomers',
  async ({ filters, skip, take }: { filters: any; skip: number; take: number }, { rejectWithValue }) => {
    const params = new URLSearchParams();
    params.append('sort', 'name_asc');
    params.append('skip', skip.toString());
    params.append('take', take.toString());

    if (filters.type?.length > 0) {
      filters.type.forEach((type: string) => {
        params.append('type', type);
      });
    }
    if (filters.searchValue) {
      params.append('searchKey', 'domains.name');
      params.append('searchKey', 'name');
      params.append('searchValue', filters.searchValue);
    }
    if (filters.priceSheet?.length > 0) {
      filters.priceSheet.forEach((sheet: string) => {
        params.append('sheet.id', sheet);
      });
    }

    try {
      const response = await apiInstance.get(`/organization?${params}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const editCustomer = createAsyncThunk('admin/editCustomer', async (info: any, { rejectWithValue }) => {
  try {
    const response = await apiInstance.patch(`/organization?organizationId=${info.id}`, info);
    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response.data.message,
      errorStack: error.response.data.errorStack || [],
    });
  }
});

export const getPriceSheets = createAsyncThunk('admin/getPriceSheets', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.get('/pricing/print-price/sheets/');
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const deleteOrganization = createAsyncThunk(
  'admin/deleteOrganization',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiInstance.delete(`/organization?organizationId=${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const getPostageRates = createAsyncThunk('admin/getPostageRates', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.get(`/pricing/postage-rate`);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const editPostageRates = createAsyncThunk(
  'admin/editPostageRates',
  async (postageRates: any, { rejectWithValue }) => {
    try {
      const response = await apiInstance.put('/pricing/postage-rate', { postageRates });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const getSheetPriceList = createAsyncThunk(
  'admin/getSheetPriceList',
  async (sheetId: string, { rejectWithValue }) => {
    try {
      const response = await apiInstance.get(`/pricing/print-price/sheets/${sheetId}/costs`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const addPriceSheet = createAsyncThunk('admin/addPriceSheet', async (sheetName: string, { rejectWithValue }) => {
  try {
    const response = await apiInstance.post(`/pricing/print-price/sheets`, {
      sheetMeta: {
        name: sheetName,
      },
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const deletePriceSheet = createAsyncThunk('admin/deletePriceSheet', async (id: string, { rejectWithValue }) => {
  try {
    const response = await apiInstance.delete(`/pricing/print-price/sheets/${id}`);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const editPriceSheetName = createAsyncThunk(
  'admin/editPriceSheetName',
  async (info: { id: string; name: string }, { rejectWithValue }) => {
    try {
      const response = await apiInstance.patch(`/pricing/print-price/sheets/${info.id}`, { name: info.name });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const editSheetPrices = createAsyncThunk('admin/editSheetPrices', async (data: any, { rejectWithValue }) => {
  try {
    const response = await apiInstance.put(`/pricing/print-price/sheets/${data.id}/costs`, {
      printPriceAdjustments: data.printPriceAdjustments,
      printPriceCosts: data.printPriceCosts,
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const getPromoCodes = createAsyncThunk(
  'admin/getPromoCodes',
  async ({ searchValue, skip, take }: { searchValue: string; skip: number; take: number }, { rejectWithValue }) => {
    try {
      const response = await apiInstance.get(
        `/pricing/promocodes?skip=${skip}&take=${take}&sort=createdAt_desc${searchValue && `&name_match=${searchValue}`}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const addPromoCode = createAsyncThunk(
  'admin/createPromoCode',
  async (
    data: { name: string; startAt: string; endAt: string; printCreditEndAt: string; printCreditAmount: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiInstance.post('/pricing/promocodes', {
        ...data,
        startAt: new Date(data.startAt),
        endAt: new Date(data.endAt),
        printCreditEndAt: new Date(data.printCreditEndAt),
        printCreditAmount: Number(data.printCreditAmount),
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const editPromoCode = createAsyncThunk(
  'admin/editPromoCode',
  async (data: { id: number; endAt: Date; printCreditEndAt: Date; isActivated?: boolean }, { rejectWithValue }) => {
    const { id, ...editCodeInfo } = data;
    try {
      const response = await apiInstance.patch(`/pricing/promocodes?promocodeId=${id}`, editCodeInfo);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const demoteMember = createAsyncThunk('admin/demoteMember', async (memberId: number, { rejectWithValue }) => {
  try {
    await apiInstance.post('/admin/members/demote', { memberId });
    return memberId;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const promoteMember = createAsyncThunk('admin/promoteMember', async (memberId: number, { rejectWithValue }) => {
  try {
    await apiInstance.post('/admin/members/promote', { memberId });
    return memberId;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const deactivateMember = createAsyncThunk(
  'admin/deactivateMember',
  async (memberId: number, { rejectWithValue }) => {
    try {
      await apiInstance.post('/admin/members/deactivate', { memberId });
      return memberId;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const changeJobStatus = createAsyncThunk(
  'admin/changeJobStatus',
  async (changeJobInfo: { jobId: number; newStatus: string }, { rejectWithValue }) => {
    try {
      if (changeJobInfo.newStatus === 'in_queue') {
        await apiInstance.post(`/job/item/${changeJobInfo.jobId}/move-to-in-queue`, {
          documentChangesSaved: true,
        });
      } else {
        await apiInstance.post(`/job/item/${changeJobInfo.jobId}/set-status`, {
          status: changeJobInfo.newStatus,
        });
      }
      return changeJobInfo.jobId;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message ?? 'Something went wrong');
    }
  },
);

export const updateJobTrackingNumbers = createAsyncThunk(
  'admin/updateJobTrackingNumbers',
  async (printDocuments: Array<{ id: number; pbTrackingNumber: string }>, { rejectWithValue }) => {
    try {
      await apiInstance.put(`/job/print-documents`, {
        printDocuments,
      });
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getPostageRatesv2 = createAsyncThunk('admin/getPostageRatesv2', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.get('/pricing-model/postage-rates');
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getMeteredRates = createAsyncThunk('admin/getMeteredRates', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.get('/pricing-model/metered-rates');
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const updatePostageRatesV2 = createAsyncThunk(
  'admin/updatePostageRatesV2',
  async (updatedRates: any, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const updatedEnvelopeRates = (state as any).admin.updatedEnvelopeRates;
      const mappedEnvelopeRates = Object.entries(updatedEnvelopeRates).map(([id, price]) => ({
        id: +id,
        price,
      }));

      const response = await apiInstance.put('/pricing-model/postage-rates', {
        uspsPostageRates: [...updatedRates, ...mappedEnvelopeRates],
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const updateMeteredInputs = createAsyncThunk(
  'admin/updateMeteredInputs',
  async (updatedMeteredInputs: Record<string, string>, { rejectWithValue }) => {
    try {
      const formattedInputs = Object.entries(updatedMeteredInputs).map(([name, price]) => ({
        name,
        price: +price.slice(1),
      }));
      const response = await apiInstance.put('/pricing-model/metered-rates', { meteredRates: formattedInputs });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const getWeightCriticalInput = createAsyncThunk(
  'admin/getWeightCriticalInput',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.get('/pricing-model/weight-inputs');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const updateWeightInputs = createAsyncThunk(
  'admin/updateWeightInputs',
  async (updatedWeightInputs: { [key: string]: string }, { rejectWithValue }) => {
    try {
      const formattedInputs = Object.entries(updatedWeightInputs).map(([name, weight]) => ({
        name,
        weight: +weight,
      }));
      const response = await apiInstance.put('/pricing-model/weight-inputs', { weightCriticalInputs: formattedInputs });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const getDocumentProcessingRates = createAsyncThunk(
  'admin/getDocumentProcessingRates',
  async (selectedSheetId: string, { rejectWithValue }) => {
    try {
      const response = await apiInstance.get(`/pricing-model/doc-rates?sheetId=${selectedSheetId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const updateDocProcessingRates = createAsyncThunk(
  'admin/updateDocProcessingRates',
  async (updatedRates: { [key: string]: string }, { rejectWithValue }) => {
    try {
      const formattedInputs = Object.entries(updatedRates).map(([id, price]) => ({
        id: +id,
        price: +price.slice(1),
      }));
      const response = await apiInstance.put('/pricing-model/doc-rates', { documentProcessingRates: formattedInputs });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const getDocSheets = createAsyncThunk('admin/getDocSheets', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.get('/pricing-model/doc-sheets');
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const createDocSheet = createAsyncThunk('admin/getDocSheets', async (name: string, { rejectWithValue }) => {
  try {
    const response = await apiInstance.post('/pricing-model/doc-sheets', { name });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const deleteDocSheet = createAsyncThunk('admin/getDocSheets', async (id: any, { rejectWithValue }) => {
  try {
    const response = await apiInstance.delete(`/pricing-model/doc-sheets?sheetId=${id}`);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const updateDocSheet = createAsyncThunk('admin/getDocSheets', async ({ id, name }: { id: string, name: string }, { rejectWithValue }) => {
  try {
    const response = await apiInstance.patch(`/pricing-model/doc-sheets?sheetId=${id}`, { name });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});